import axios, {
  AxiosError,
  AxiosResponse,
  InternalAxiosRequestConfig,
} from 'axios';
import get from 'lodash/get';
import set from 'lodash/set';
import startsWith from 'lodash/startsWith';

import { notifySignedOut } from './util/sign-out-listeners';

export default class ResponseInterceptors {
  basePath = '';
  sassReportPath = '';
  disallowedRefreshTokenUrlsPrefix: string[] = [];

  isRefreshingInProgress = false;
  failedQueue: { resolve: any; reject: any }[] = [];
  environment = '';
  client: 'portal' | 'admin';

  constructor(
    basePath: string,
    sassReportPath: string,
    excludedRefreshTokenRoutes: string[],
    environment: string,
    client: 'portal' | 'admin'
  ) {
    this.basePath = basePath;
    this.sassReportPath = sassReportPath;
    this.disallowedRefreshTokenUrlsPrefix = excludedRefreshTokenRoutes;
    this.environment = environment;
    this.client = client;
  }

  processQueue = (error: unknown | null, token: string | null) => {
    this.failedQueue.forEach((queuedRequest: any) => {
      if (error) {
        queuedRequest.reject(error);
      } else {
        queuedRequest.resolve(token);
      }
    });
    this.failedQueue = [];
  };

  handleSignOut = () => {
    if (localStorage.getItem('showNavBar') === 'false') {
      // don't do anything
    } else {
      localStorage.removeItem('access_token');
      localStorage.removeItem('refresh_token');
      notifySignedOut();
      if (window.location.pathname !== '/auth/sign-in') {
        window.location.pathname = '/';
      }
    }
  };

  getRefreshAccessToken = async (accessToken: string, refreshToken: string) => {
    const response = await fetch(`${this.basePath}/Account/refresh-token`, {
      method: 'post',
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json',
        environment: this.environment,
        client: this.client,
      },
      body: JSON.stringify({ token: accessToken, refreshToken }),
    });
    if (!response.ok) {
      throw new Error('Session expired');
    }
    return response.json();
  };

  onFulfilled = (config: AxiosResponse) => config;
  onRejected = async (error: AxiosError) => {
    const originalRequest = error.config as InternalAxiosRequestConfig;
    const httpStatus = error?.response?.status;
    const url = get(originalRequest, 'url', '') as string;
    const urlPostfix = url
      .replace(this.basePath, '')
      .replace(this.sassReportPath, '');

    const isRetriedRequest = get(originalRequest, 'retry');
    const disallowedRefreshToken = this.disallowedRefreshTokenUrlsPrefix.some(
      (postFix) => startsWith(urlPostfix, postFix)
    );

    if (
      httpStatus === 401 &&
      !isRetriedRequest &&
      !disallowedRefreshToken &&
      this.client === 'portal'
    ) {
      if (this.isRefreshingInProgress) {
        return new Promise((resolve, reject) =>
          this.failedQueue.push({
            resolve,
            reject,
          })
        )
          .then((token) => {
            originalRequest.headers.Authorization = `Bearer ${token}`;
            return axios(originalRequest);
          })
          .catch((refreshingError) => refreshingError);
      }
      set(originalRequest, 'retry', true);
      this.isRefreshingInProgress = true;

      // eslint-disable-next-line no-async-promise-executor
      return new Promise(async (resolve, reject) => {
        try {
          const oldAccessToken = localStorage.getItem('access_token') as string;
          const oldRefreshToken = localStorage.getItem(
            'refresh_token'
          ) as string;
          const response = await this.getRefreshAccessToken(
            oldAccessToken,
            oldRefreshToken
          );

          const updatedAccessToken = response.token;
          const updatedRefreshToken = response.refreshToken;
          localStorage.setItem('access_token', updatedAccessToken);
          localStorage.setItem('refresh_token', updatedRefreshToken);
          this.processQueue(null, updatedAccessToken);
          resolve(axios(originalRequest));
        } catch (refreshTokenError) {
          this.processQueue(refreshTokenError, null);
          reject(refreshTokenError);
          this.handleSignOut();
        } finally {
          this.isRefreshingInProgress = false;
        }
      });
    }
    if (httpStatus === 401 && (isRetriedRequest || this.client === 'admin')) {
      this.handleSignOut();
    }
    if (httpStatus === 400) {
      const message = get(error, 'response.data.message', '') as string;
      if (message === 'Your session has expired. Please log in again.') {
        this.handleSignOut();
        return Promise.reject(error);
      }
    }
    return Promise.reject(error);
  };
}
